<template>
  <header class="fixed top-0 left-0 right-0 z-50 w-full max-w-full overflow-hidden">
    <!-- Total height split: 1/3 announcement, 2/3 nav -->
    <div class="h-20 sm:h-24 md:h-26 flex flex-col">
      <!-- 1/3: Announcement -->
      <a href="https://www.starvara.com/" target="_blank" rel="noopener noreferrer" class="announcement-bar flex-1 border-b border-indigo-400/30 relative overflow-hidden block hover:bg-indigo-400/10 transition-colors duration-200 cursor-pointer">
        <div class="max-w-6xl xl:max-w-7xl 2xl:max-w-[90rem] mx-auto px-4 h-full relative z-10">
          <div class="h-full flex items-center justify-center">
            <span class="text-white text-xs sm:text-sm md:text-base select-none text-center">
              <span class="font-bold tracking-wide">STARVARA</span>
              <span class="font-medium mx-1.5 sm:mx-2">•</span>
              <span class="font-normal">$10,000 Tournament Is Live | Join Now</span>
            </span>
          </div>
        </div>
      </a>

      <!-- 2/3: Main nav -->
      <nav class="flex-[2] w-full max-w-full bg-gradient-to-br from-[#080C19] to-[#101832] border-b border-slate-800/60 overflow-hidden">
        <div class="max-w-6xl xl:max-w-7xl 2xl:max-w-[90rem] mx-auto px-3 sm:px-4 h-full">
          <div class="h-full flex items-center justify-between gap-3">
            <!-- Left: Logo + brand + nav -->
            <div class="flex items-center gap-2 sm:gap-4 lg:gap-12 min-w-0 h-full overflow-hidden">
              <div class="flex items-center gap-2 sm:gap-3 shrink-0">
                <!-- Mobile Logo -->
                <img
                  :src="logoMobile"
                  alt="VRX"
                  class="h-8 w-auto select-none sm:hidden"
                  draggable="false"
                />
                <!-- Desktop Logo -->
                <img
                  :src="logoUrl"
                  alt="VRX"
                  class="hidden sm:block h-10 md:h-10 w-auto select-none"
                  draggable="false"
                />
              </div>

              <!-- Nav Links -->
              <ul
                class="flex items-stretch h-full gap-3 sm:gap-6 lg:gap-12 text-sm sm:text-base"
                style="font-family: Inter, ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
              >
                <li class="flex items-stretch shrink-0">
                  <RouterLink
                    to="/buy"
                    class="nav-link"
                    :class="{ 'active': $route.path === '/buy' }"
                  >
                    Buy VRX
                  </RouterLink>
                </li>
                <li class="flex items-stretch shrink-0">
                  <RouterLink
                    to="/stake"
                    class="nav-link"
                    :class="{ 'active': $route.path === '/stake' }"
                  >
                    Stake VRX
                  </RouterLink>
                </li>
                <li v-if="devMode" class="flex items-stretch shrink-0">
                  <RouterLink
                    to="/dev"
                    class="nav-link"
                    :class="{ 'active': $route.path === '/dev' }"
                  >
                    Dev
                  </RouterLink>
                </li>
              </ul>
            </div>

            <!-- Right: Wallet (emit connect) -->
            <div class="flex items-center shrink-0 gap-1 sm:gap-2">
              <template v-if="account">
                <!-- Balances - hidden on very small screens -->
                <div class="hidden sm:flex items-center gap-1.5 sm:gap-2">
                  <!-- ETH -->
                  <div class="wallet-pill">
                    <img :src="iconETH" alt="ETH" class="h-5 w-5 object-contain" />
                    <span v-if="!isLoadingBalances" class="font-mono" title="ETH">{{ eth || '0' }}</span>
                    <div v-else class="loading-spinner" title="Loading ETH balance"></div>
                  </div>
                  <!-- USDT -->
                  <div class="wallet-pill">
                    <img :src="iconUSDT" alt="USDT" class="h-5 w-5 object-contain" />
                    <span v-if="!isLoadingBalances" class="font-mono" title="USDT">{{ usdt || '0' }}</span>
                    <div v-else class="loading-spinner" title="Loading USDT balance"></div>
                  </div>
                </div>

                <!-- Address -->
                <div class="wallet-address" :title="account">
                  {{ short(account) }}
                </div>
              </template>

              <button
                v-else
                @click="emit('connect')"
                class="btn-primary"
              >
                Connect Wallet
              </button>
            </div>
          </div>
        </div>
      </nav>
    </div>
  </header>
</template>

<script setup>
  import { RouterLink } from 'vue-router'
  import { defineProps, defineEmits } from 'vue'
  import logoUrl from '../assets/battle_royale.png'
  import logoMobile from '../assets/Logo Mobile.svg'
  import iconUSDT from '../assets/USDT_Logo.png'
  import iconETH  from '../assets/ETH.png'

  const props = defineProps({
    account: { type: String, default: '' },
    eth:     { type: String, default: '' },   // formatted string
    usdt:    { type: String, default: '' },   // formatted string
    isLoadingBalances: { type: Boolean, default: false },
    devMode: { type: Boolean, default: false }
  })
  const emit = defineEmits(['connect'])

  const short = (addr) => (addr ? addr.slice(0, 6) + '...' + addr.slice(-4) : '')
</script>

<style scoped>
/* Announcement Bar */
.announcement-bar {
  background: #5865F2;
  position: relative;
  overflow: hidden;
}

.announcement-bar::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 200%;
  height: 100%;
  background: linear-gradient(
    90deg,
    rgba(88, 101, 242, 0) 0%,
    rgba(114, 137, 218, 0.4) 20%,
    rgba(165, 115, 230, 0.6) 40%,
    rgba(214, 171, 243, 0.8) 50%,
    rgba(165, 115, 230, 0.6) 60%,
    rgba(114, 137, 218, 0.4) 80%,
    rgba(88, 101, 242, 0) 100%
  );
  animation: wave 6s ease-in-out infinite;
}

@keyframes wave {
  0% {
    transform: translateX(0);
    opacity: 0.6;
  }
  50% {
    opacity: 1;
  }
  100% {
    transform: translateX(100%);
    opacity: 0.6;
  }
}

/* Nav Links */
.nav-link {
  @apply flex items-center px-1 text-slate-300 transition-colors duration-200 relative;
  padding-bottom: 2px;
}

.nav-link::after {
  content: '';
  @apply absolute bottom-0 left-0 right-0 h-0.5 bg-sky-400 transition-all duration-200;
  transform: scaleX(0);
  transform-origin: center;
}

.nav-link:hover {
  @apply text-sky-300;
}

.nav-link:hover::after {
  transform: scaleX(1);
}

.nav-link.active {
  @apply text-sky-300;
  font-weight: 600;
}

.nav-link.active::after {
  transform: scaleX(1);
  @apply bg-sky-400;
}

/* Wallet Pills */
.wallet-pill {
  @apply flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-slate-800/60 border border-slate-700/50 text-slate-200 text-sm;
  backdrop-filter: blur(10px);
  min-width: 70px;
}

/* Loading Spinner */
.loading-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(148, 163, 184, 0.3);
  border-top: 2px solid #38bdf8;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Wallet Address */
.wallet-address {
  @apply px-3 py-2 rounded-lg bg-gradient-to-r from-sky-500/20 to-blue-500/20 border border-sky-400/30 text-sky-200 text-sm font-mono;
  backdrop-filter: blur(10px);
}

/* Primary Button */
.btn-primary {
  @apply px-4 py-2 rounded-lg bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-400 hover:to-blue-500 text-white font-medium text-sm transition-all duration-200 shadow-lg hover:shadow-xl;
}

.btn-primary:hover {
  transform: translateY(-1px);
}

@media (max-width: 640px) {
  .nav-link {
    font-size: 0.875rem;
  }
  
  .wallet-pill {
    min-width: 60px;
    @apply px-2 py-1 text-xs;
  }
  
  .wallet-address {
    @apply px-2.5 py-1.5 text-xs;
  }
  
  .btn-primary {
    @apply px-3 py-1.5 text-xs;
  }
}
</style>
